import { Link } from "react-router-dom";
import { MapPin, Truck, ShieldCheck, ArrowRight } from "lucide-react";
import { DELIVERY_ZONES, BRAND } from "@/lib/constants";
import WaitlistForm from "@/components/WaitlistForm";

export default function AreasServed() {
  return (
    <div data-testid="areas-served-page">
      <section className="py-14 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="max-w-3xl animate-fade-up">
            <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
              Areas Served
            </p>
            <h1 className="mt-4 font-display text-[32px] leading-[1.1] sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
              Serving Bethesda &{" "}
              <span className="text-[#28C0D8]">surrounding communities.</span>
            </h1>
            <p className="mt-6 text-[17px] text-[#94A3B8] max-w-2xl">
              From our flagship at {BRAND.address.line1}, {BRAND.address.line2},
              we plan to deliver throughout Montgomery County and select
              Washington DC ZIP codes at launch. Tap any ZIP for local
              delivery details.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-14 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {DELIVERY_ZONES.map((zone, i) => (
              <div
                key={zone.slug}
                className="group rounded-2xl border border-white/10 bg-[#1C2028] p-7 hover:border-[#28C0D8]/40 hover:-translate-y-1 transition-all duration-300"
                data-testid={`area-zone-${i}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl font-semibold text-white tracking-tight">
                      {zone.city}
                    </h3>
                    {zone.note && (
                      <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-[#28C0D8]">
                        {zone.note}
                      </p>
                    )}
                  </div>
                  <MapPin className="h-5 w-5 text-[#28C0D8] shrink-0" />
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                  {zone.zips.map((z) => (
                    <Link
                      key={`${zone.slug}-${z}`}
                      to={`/areas-served/${z}`}
                      className="inline-flex items-center rounded-full border border-white/10 bg-[#13161C] px-3 py-1 text-sm font-medium text-[#28C0D8] hover:border-[#28C0D8]/60 hover:bg-[#28C0D8]/10 transition"
                      data-testid={`area-zip-link-${z}`}
                    >
                      {z}
                    </Link>
                  ))}
                </div>
                <Link
                  to={`/areas-served/${zone.zips[0]}`}
                  className="mt-5 inline-flex items-center gap-2 text-sm text-[#28C0D8] hover:gap-3 transition-all"
                >
                  {zone.city} delivery details <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-[#0A0B0E]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-2xl border border-white/10 bg-[#13161C] p-7">
              <Truck className="h-8 w-8 text-[#28C0D8] mb-5" />
              <h3 className="font-display text-lg font-semibold text-white">
                Reliable local delivery
              </h3>
              <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">
                Trained drivers handling every Maryland-compliant delivery
                end-to-end.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#13161C] p-7">
              <ShieldCheck className="h-8 w-8 text-[#28C0D8] mb-5" />
              <h3 className="font-display text-lg font-semibold text-white">
                ID-verified at the door
              </h3>
              <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">
                Every handoff verified — 21+ or Maryland medical card required.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#13161C] p-7">
              <MapPin className="h-8 w-8 text-[#28C0D8] mb-5" />
              <h3 className="font-display text-lg font-semibold text-white">
                Don&apos;t see your ZIP?
              </h3>
              <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">
                Join the waitlist — tell us where you&apos;re located and
                we&apos;ll notify you as coverage expands.
              </p>
            </div>
          </div>

          <div className="mt-14 rounded-3xl border border-white/10 bg-[#13161C] p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold text-white tracking-tight">
                  Be first on our delivery route.
                </h3>
                <p className="mt-3 text-[15px] text-[#94A3B8]">
                  Join the waitlist for priority launch delivery slots.
                </p>
              </div>
              <WaitlistForm source="areas_served" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
